// Used in many components

import { Injectable } from "@angular/core";
import { EditionId, LevelId, SubjectId } from "@carnegie-learning/common-types";

@Injectable()
export class LanguageHelper {
    private IPAGuideBaseUrl = "http://unit.myemcp.com/";
    constructor() {}

    isMathProgram(subjectId: SubjectId | undefined): boolean {
        return subjectId === "MATH";
    }

    // Math Program is currently languageID === 333
    ifMathLanguage(editionId: string | number | undefined): boolean {
        // TODO: This will eventually need to cease supporting numbers.
        return (
            editionId === "MATH" ||
            editionId === "MFLEX" ||
            editionId === 333 ||
            editionId === "333"
        );
    }
    getMathLanguageID(): number {
        return 333;
    }

    getMathPrettyLevelList() {
        return {
            1: "Foundations to Middle School",
            2: "6th Grade Mathematics",
            3: "7th Grade Mathematics",
            4: "8th Grade Mathematics",
            5: "Algebra I",
            6: "Algebra II",
            7: "Geometry",
            8: "Integrated Math I",
            9: "Integrated Math II",
            10: "Integrated Math III",
            11: "Financial Literacy",
        };
    }

    getMathPrettyLevelColumnRange() {
        return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
    }

    getPrettyLevelValueToDB(index: number) {
        const prettyLevelToDBValue = [10, 7, 8, 9, 1, 2, 3, 4, 5, 6, 11];
        return index ? prettyLevelToDBValue[index - 1] : index;
    }

    getPrettyLevelArrayToValueToDBArray(indexArray: number[]) {
        const updatedArray: number[] = [];

        indexArray.forEach((value) => {
            updatedArray.push(this.getPrettyLevelValueToDB(value));
        });

        return updatedArray;
    }

    getDBLevelValueToPrettyValue(index: number) {
        const DBLevelToPrettyLevelValue = [5, 6, 7, 8, 9, 10, 2, 3, 4, 1, 11];
        return index ? DBLevelToPrettyLevelValue[index - 1] : index;
    }

    getDBLevelArrayToPrettyArray(indexArray: number[]) {
        const updatedArray: number[] = [];

        if (indexArray) {
            indexArray.forEach((value) => {
                updatedArray.push(this.getDBLevelValueToPrettyValue(value));
            });
        }

        return updatedArray;
    }

    // We don't currently have a great way to get the specific Edition icon cleanly
    // So this current method will have to do for now.
    getSubjectLogo(subjectId: SubjectId, editionId: EditionId) {
        let string;
        switch (subjectId) {
            case "BIOTECH":
                string = "biotech";
                break;
            case "GER":
                if (editionId === "GER-I") {
                    string = "immersion";
                } else if (editionId === "GER-EXP") {
                    string = "exploring";
                } else {
                    string = "GER-deutsch";
                }
                break;
            case "ITA":
                string = "ITA-amici";
                break;
            case "LANG":
                string = "mirrors-windows";
                break;
            case "ESP":
                if (editionId === "ESP-EVA") {
                    string = "en-vos-alta";
                } else if (editionId === "ESP-I") {
                    string = "immersion";
                } else if (editionId === "ESP-EXP") {
                    string = "exploring";
                } else if (editionId === "ESP-A") {
                    string = "aventura";
                } else {
                    string = "ESP-que";
                }
                break;
            case "CHI":
                string = "CHI-zhen";
                break;
            case "COMP-SCI":
                string = "COMP-SCI-computer-science";
                break;
            case "ECON":
                string = "ECON-economics";
                break;
            case "FRA":
                if (editionId === "FRA-I") {
                    string = "immersion";
                } else if (editionId === "FRA-EXP") {
                    string = "exploring";
                } else {
                    string = "FRA-tes";
                }
                break;
            default:
                string = "CL-bug-blank";
                break;
        }
        return string;
    }

    // Check if World Language program
    ifWorldLanguageProgram(id: number | string): boolean {
        // TODO (FL) this need to update for subjectId
        // id = languageID - 1, 2, 3, 4, 46, 55, 133, 155, 290, 291,
        // 336, 394, 396, 398, 400, 402, 409, 411, 413, 415, 417, 419, 421
        return (
            (typeof id === "string" &&
                ["CHI", "ESP", "FRA", "GER", "ITA"].includes(id)) ||
            id === 1 ||
            id === 2 ||
            id === 3 ||
            id === 4 ||
            id === 46 ||
            id === 55 ||
            id === 133 ||
            id === 155 ||
            id === 290 ||
            id === 291 ||
            id === 336 ||
            id === 394 ||
            id === 396 ||
            id === 398 ||
            id === 400 ||
            id === 402 ||
            id === 409 ||
            id === 411 ||
            id === 413 ||
            id === 415 ||
            id === 417 ||
            id === 419 ||
            id === 421
        );
    }

    getIPAIntegrationGuideUrl(subjectId: SubjectId) {
        let url = this.IPAGuideBaseUrl;
        switch (subjectId) {
            case "GER":
                url = url + "daintegration";
                break;
            case "ITA":
                url = url + "amintegration";
                break;
            case "ESP":
                url = url + "qcintegration";
                break;
            case "CHI":
                url = url + "zbintegration";
                break;
            case "FRA":
                url = url + "tebintegration";
                break;
            default:
                break;
        }
        return url;
    }
    getIPAScoringGuideUrl(levelId: LevelId) {
        if (levelId) {
            return this.IPAGuideBaseUrl + "WLscoring" + levelId;
        }
    }
    gradeLevelReturn(subjectId: string): string {
        if (this.ifWorldLanguageProgram(subjectId) || subjectId === "MATH") {
            return "Level";
        } else if (subjectId === "COMP-SCI") {
            return "Course";
        } else {
            return "Grade";
        }
    }

    getLocale(str: string): string {
        let languageName: string;
        switch (str.slice(0, 2)) {
            case "en":
                languageName = "English";
                break;
            case "es":
                languageName = "Spanish";
                break;
            default:
                languageName = "";
        }
        return languageName;
    }

    getLocaleAbbr(str: string): string {
        let abbreviation = "";
        switch (str.toLowerCase()) {
            case "english":
                abbreviation = "en";
                break;
            case "spanish":
                abbreviation = "es";
                break;
        }
        return abbreviation;
    }

    isPerformanceEdition(editionId: string): boolean {
        return [
            "ESP-1",
            "ESP-2",
            "ESP-C2",
            "ESP-2-CA",
            "ESP-2-MIA",
            "FRA-1",
            "FRA-2",
            "FRA-C2",
            "FRA-C2-CA",
            "GER-7",
            "GER-C7",
            "GER-8",
            "GER-C8",
            "CHI-1",
            "CHI-2",
            "CHI-3",
        ].includes(editionId);
    }
}
